import { matchPattern } from './matcher-cache';

/**
 * Recursively compares two items (objects, arrays, primitives) to check if the second item
 * is a "subset" of the first. For strings, this comparison uses glob matching.
 *
 * @param item1 The first item to compare (typically the larger/containing item).
 * @param item2 The second item to compare (typically the subset or pattern to check for).
 * @returns {boolean} `true` if `item2` is a subset of `item1`, otherwise `false`.
 */
export const compareObjects = (item1: any, item2: any): boolean => {
  // If item2 is a primitive or null, perform comparison.
  if (typeof item2 !== 'object' || item2 === null) {
    // Use glob matching for strings (item2 is the pattern). matchPattern caches
    // the compiled matcher per pattern and fast-paths literal strings to ===
    // (EID-64) instead of recompiling the glob on every call.
    if (typeof item1 === 'string' && typeof item2 === 'string') {
      return matchPattern(item1, item2);
    }
    // Use strict equality for other primitives.
    return item1 === item2;
  }

  // If item2 is an object but item1 is not, they can't match.
  if (typeof item1 !== 'object' || item1 === null) {
    return false;
  }

  // Handle arrays: check if every element in item2 has a matching element in item1.
  if (Array.isArray(item2)) {
    if (!Array.isArray(item1)) {
      return false;
    }
    return item2.every((subItem2) =>
      item1.some((subItem1) => compareObjects(subItem1, subItem2)),
    );
  }

  // Handle objects: check if all key-value pairs in item2 exist and match in item1.
  for (const [key, value2] of Object.entries(item2)) {
    if (!(key in item1)) {
      return false;
    }
    if (!compareObjects(item1[key], value2)) {
      return false;
    }
  }

  return true;
};
